import React, { useState, useEffect, useMemo } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { useSidebar } from '../../contexts/SidebarContext';
import HeaderNavigation from '../../components/ui/HeaderNavigation';
import MainSidebar from '../../components/ui/MainSidebar';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import NotificationToast from '../../components/ui/NotificationToast';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import EditDayAttendanceModal from './components/EditDayAttendanceModal';
import attendanceService from '../../services/attendanceService';

const AttendanceTracker = () => {
  const { isCollapsed } = useSidebar();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [matrixData, setMatrixData] = useState(null);
  const [notifications, setNotifications] = useState([]);

  // Edit Modal State
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [editingDay, setEditingDay] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const user = {
    name: "HR Administrator",
    email: "hr@facilitypro.com",
    role: "HR Manager"
  };

  useEffect(() => {
    fetchMatrix();
  }, [selectedPeriod]);

  const fetchMatrix = async () => {
    try {
      setLoading(true);
      setError(null);

      const [year, month] = selectedPeriod.split('-');
      const res = await attendanceService.getAttendanceMatrix({
        year: parseInt(year),
        month: parseInt(month)
      });

      if (res.success && res.data) {
        setMatrixData(res.data);
      } else {
        setMatrixData(null);
        setError(res.error || 'Failed to load attendance matrix');
      }
    } catch (err) {
      console.error('[AttendanceTracker] Fetch error:', err);
      setError('Failed to load attendance data');
      setMatrixData(null);
    } finally {
      setLoading(false);
    }
  };

  const generatePeriodOptions = () => {
    const options = [];
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    const startDate = new Date(currentYear, currentMonth - 23, 1);
    const endDate = new Date(currentYear, currentMonth + 3, 1);

    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

    let date = new Date(startDate);
    while (date <= endDate) {
      const year = date.getFullYear();
      const month = date.getMonth();
      const value = `${year}-${String(month + 1).padStart(2, '0')}`;
      const label = `${monthNames[month]} ${year}`;
      options.push({ value, label });
      date.setMonth(date.getMonth() + 1);
    }

    return options.reverse();
  };

  const periodOptions = useMemo(() => generatePeriodOptions(), []);

  const addNotification = (n) => {
    setNotifications(prev => [n, ...prev.slice(0, 4)]);
  };

  const handleCellClick = (emp, dayNum, dayData) => {
    if (dayData && dayData.is_future) {
      addNotification({
        id: Date.now(),
        type: 'warning',
        title: 'Future Date',
        message: `Attendance cannot be recorded for future dates`,
        timestamp: new Date()
      });
      return;
    }

    const [year, month] = selectedPeriod.split('-');
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;

    setEditingEmployee({
      id: emp.id,
      full_name: emp.name || emp.full_name
    });

    setEditingDay({
      day: dayNum,
      date: dateStr,
      status: dayData?.status || 'present',
      worked_hours: dayData?.hours ?? dayData?.worked_hours ?? null,
      check_in_time: dayData?.check_in_time || null,
      check_out_time: dayData?.check_out_time || null,
      remarks: dayData?.remarks || null
    });

    setShowEditModal(true);
  };

  const handleRecordSaved = () => {
    addNotification({
      id: Date.now(),
      type: 'success',
      title: 'Attendance Updated',
      message: 'Attendance record saved successfully',
      timestamp: new Date()
    });
    fetchMatrix();
  };

  const handleExportCSV = () => {
    const [year, month] = selectedPeriod.split('-');
    window.open(`/api/attendance/export?month=${month}&year=${year}&format=csv`, '_blank');
    addNotification({
      id: Date.now(),
      type: 'success',
      title: 'Export Triggered',
      message: `Exporting attendance matrix for ${selectedPeriod}`,
      timestamp: new Date()
    });
  };

  // Extract employees list (supports both api structures)
  const employeeList = useMemo(() => {
    if (!matrixData) return [];
    const list = matrixData.employees || matrixData.matrix || [];
    const s = searchTerm.toLowerCase().trim();
    return list.filter(emp => {
      const name = (emp.name || emp.full_name || '').toLowerCase();
      const code = (emp.employee_code || '').toLowerCase();
      const dept = (emp.department || '').toLowerCase();
      return !s || name.includes(s) || code.includes(s) || dept.includes(s);
    });
  }, [matrixData, searchTerm]);

  // Extract trend chart dataset
  const trendDataset = useMemo(() => {
    if (!matrixData) return [];
    if (matrixData.trend) {
      const [year, month] = selectedPeriod.split('-');
      const monthShorts = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthAbbr = monthShorts[parseInt(month) - 1] || 'Aug';

      return matrixData.trend.map(t => ({
        day: t.day,
        label: `${t.day} ${monthAbbr}`,
        Present: t.present ?? t.present_count ?? 0,
        Absent: t.absent ?? t.absent_count ?? 0
      }));
    }
    return [];
  }, [matrixData, selectedPeriod]);

  // Days list 1..31
  const daysList = useMemo(() => {
    if (matrixData?.days) return matrixData.days;
    const daysInMonth = matrixData?.period?.days_in_month || 31;
    return Array.from({ length: daysInMonth }, (_, i) => i + 1);
  }, [matrixData]);

  const [year, month] = selectedPeriod.split('-');
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const monthName = monthNames[parseInt(month) - 1] || 'August';

  return (
    <div className="min-h-screen bg-background">
      <HeaderNavigation
        onSidebarToggle={() => setSidebarOpen(!sidebarOpen)}
        user={user}
        notifications={notifications}
      />

      <MainSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <main className={`pt-16 transition-all duration-300 ${isCollapsed ? 'lg:ml-16' : 'lg:ml-72'}`}>
        <div className="p-6 min-w-0 max-w-full overflow-hidden">
          <BreadcrumbNavigation />

          {/* Page Header */}
          <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-foreground flex items-center gap-2">
                <span className="font-extrabold text-foreground">Attendance</span>
                <span className="font-normal text-teal-600 dark:text-teal-400">Tracker</span>
              </h1>
              <p className="text-muted-foreground text-sm mt-1">
                Enter and export attendance data • Standard: <span className="font-semibold text-primary">8.0 hours/day</span>
              </p>
            </div>

            <div className="flex items-center gap-3">
              <Select
                options={periodOptions}
                value={selectedPeriod}
                onChange={setSelectedPeriod}
                className="w-48"
              />
              <Button
                variant="outline"
                onClick={handleExportCSV}
                iconName="Download"
                iconPosition="left"
              >
                Export CSV
              </Button>
            </div>
          </div>

          {/* Month Section Banner & Search Bar */}
          <div className="bg-card border border-border rounded-xl p-4 mb-6 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-bold text-foreground">
                Attendance — {monthName} {year}
              </h2>
              <p className="text-xs text-muted-foreground">
                Live Employee Roster & Attendance Matrix
              </p>
            </div>

            <div className="w-full md:w-80">
              <Input
                type="text"
                placeholder="Filter by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                icon="Search"
              />
            </div>
          </div>

          {/* Recharts Trend Chart */}
          {trendDataset.length > 0 && (
            <div className="bg-card border border-border rounded-xl p-6 mb-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-bold text-foreground flex items-center gap-2">
                  <Icon name="BarChart2" size={18} className="text-teal-600 dark:text-teal-400" />
                  Attendance Trend — Daily Present vs Absent Employee Count
                </h3>
              </div>

              <div className="h-60 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={trendDataset} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.2} />
                    <XAxis dataKey="label" tickLine={false} tick={{ fontSize: 10 }} />
                    <YAxis allowDecimals={false} tickLine={false} tick={{ fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'var(--bg-card, #ffffff)',
                        borderColor: 'var(--border, #e5e7eb)',
                        borderRadius: '8px',
                        fontSize: '12px'
                      }}
                    />
                    <Legend wrapperStyle={{ paddingTop: '8px' }} />
                    <Bar dataKey="Present" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={18} />
                    <Bar dataKey="Absent" fill="#ef4444" radius={[4, 4, 0, 0]} maxBarSize={18} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Employee x Day Grid */}
          {loading ? (
            <div className="bg-card border border-border rounded-xl p-16 text-center shadow-sm">
              <Icon name="Loader2" size={32} className="animate-spin text-teal-600 mx-auto mb-3" />
              <p className="text-muted-foreground text-sm font-medium">Loading attendance matrix...</p>
            </div>
          ) : error ? (
            <div className="bg-card border border-border rounded-xl p-12 text-center shadow-sm">
              <p className="text-rose-500 mb-4 font-semibold">{error}</p>
              <Button onClick={fetchMatrix} variant="outline">
                Retry Loading
              </Button>
            </div>
          ) : employeeList.length === 0 ? (
            <div className="bg-card border border-border rounded-xl p-12 text-center shadow-sm">
              <Icon name="CalendarX" size={36} className="text-muted-foreground mx-auto mb-2" />
              <p className="text-muted-foreground font-medium">No employee records found for {monthName} {year}.</p>
              <p className="text-xs text-muted-foreground mt-1">Adjust search filter or select another period.</p>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
              {/* Scroll Hint Header */}
              <div className="bg-muted/40 px-4 py-2.5 border-b border-border flex items-center justify-between text-xs text-muted-foreground font-medium">
                <span className="flex items-center gap-1.5">
                  <Icon name="ArrowRightLeft" size={14} className="text-teal-600 dark:text-teal-400" />
                  Scroll horizontally · Name and Summary columns stay fixed
                </span>
                <span className="font-semibold text-foreground">Showing {employeeList.length} employees</span>
              </div>

              {/* Horizontally Scrollable Table Container */}
              <div className="overflow-x-auto relative whitespace-nowrap">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-muted/70 border-b border-border">
                      {/* Sticky Employee Header */}
                      <th
                        className="py-3 px-4 font-bold text-foreground min-w-[200px] border-r border-border shadow-sm"
                        style={{ position: 'sticky', left: 0, zIndex: 2, background: 'var(--bg-card, #ffffff)' }}
                      >
                        EMPLOYEE
                      </th>

                      {/* 1..31 Day Columns Headers */}
                      {daysList.map(d => {
                        const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                        const w = new Date(dateStr).getDay();
                        const isWeekend = (w === 0 || w === 6);

                        return (
                          <th
                            key={d}
                            className={`py-2 px-1 text-center min-w-[42px] max-w-[46px] border-r border-border/60 ${
                              isWeekend ? 'bg-amber-500/10 text-amber-600 font-bold' : 'text-foreground'
                            }`}
                          >
                            <div className="font-bold text-[11px]">{d}</div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {employeeList.map(emp => {
                      const empName = emp.name || emp.full_name || 'Employee';

                      return (
                        <tr key={emp.id} className="hover:bg-muted/30 transition-colors">
                          {/* Sticky Employee Name Cell */}
                          <td
                            className="py-3 px-4 border-r border-border font-semibold text-foreground truncate max-w-[200px]"
                            style={{ position: 'sticky', left: 0, zIndex: 2, background: 'var(--bg-card, #ffffff)' }}
                            title={empName}
                          >
                            {empName}
                          </td>

                          {/* 1..31 Day Matrix Cells */}
                          {daysList.map(d => {
                            let cellData = null;
                            if (emp.cells) {
                              cellData = emp.cells[d] || emp.cells[String(d)];
                            } else if (emp.days) {
                              cellData = emp.days[d] || emp.days[String(d)];
                            }

                            const status = cellData?.status;
                            const hours = cellData?.hours ?? cellData?.worked_hours;

                            let cellContent = null;
                            let cellClass = 'hover:bg-muted/40 cursor-pointer';

                            if (cellData === null || status === null) {
                              // Future date - blank
                              cellContent = null;
                              cellClass = 'bg-muted/10 cursor-not-allowed';
                            } else if (status === 'present') {
                              cellContent = (
                                <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                                  {hours !== null && hours !== undefined ? Number(hours).toFixed(1) : '8.0'}
                                </span>
                              );
                              cellClass = 'bg-emerald-500/10 hover:bg-emerald-500/20 cursor-pointer';
                            } else if (status === 'late') {
                              cellContent = (
                                <span className="font-semibold text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1">
                                  {hours !== null && hours !== undefined ? Number(hours).toFixed(1) : '8.0'}
                                  <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0" title="Late Arrival" />
                                </span>
                              );
                              cellClass = 'bg-emerald-500/10 hover:bg-emerald-500/20 cursor-pointer';
                            } else if (status === 'half_day') {
                              cellContent = (
                                <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-500/20 text-blue-600 dark:text-blue-300">
                                  HD {hours !== null && hours !== undefined ? Number(hours).toFixed(1) : ''}
                                </span>
                              );
                              cellClass = 'bg-blue-500/10 hover:bg-blue-500/20 cursor-pointer';
                            } else if (status === 'paid_leave') {
                              cellContent = (
                                <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-500/20 text-purple-600 dark:text-purple-300">
                                  PL
                                </span>
                              );
                              cellClass = 'bg-purple-500/10 hover:bg-purple-500/20 cursor-pointer';
                            } else if (status === 'leave') {
                              cellContent = (
                                <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-500/20 text-amber-600 dark:text-amber-400">
                                  L
                                </span>
                              );
                              cellClass = 'bg-amber-500/10 hover:bg-amber-500/20 cursor-pointer';
                            } else if (status === 'week_off') {
                              cellContent = (
                                <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold bg-gray-500/20 text-gray-600 dark:text-gray-400">
                                  WO
                                </span>
                              );
                              cellClass = 'bg-gray-500/10 hover:bg-gray-500/20 cursor-pointer';
                            } else if (status === 'sunday') {
                              cellContent = (
                                <span className="text-[11px] font-normal text-muted-foreground/60 select-none">
                                  Sun
                                </span>
                              );
                              cellClass = 'bg-gray-500/5 hover:bg-muted/40 cursor-pointer';
                            } else if (status === 'absent') {
                              cellContent = (
                                <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold bg-rose-500/20 text-rose-600 dark:text-rose-400">
                                  A
                                </span>
                              );
                              cellClass = 'bg-rose-500/10 hover:bg-rose-500/20 cursor-pointer';
                            }

                            return (
                              <td
                                key={d}
                                onClick={() => handleCellClick(emp, d, cellData)}
                                className={`py-2 px-1 text-center border-r border-border/60 text-[11px] font-mono transition-colors ${cellClass}`}
                              >
                                {cellContent}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Edit Single Day Modal */}
      <EditDayAttendanceModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingEmployee(null);
          setEditingDay(null);
        }}
        employee={editingEmployee}
        dayData={editingDay}
        onSaved={handleRecordSaved}
      />

      <NotificationToast
        notifications={notifications}
        onDismiss={(id) => setNotifications(prev => prev.filter(n => n.id !== id))}
        position="top-right"
      />
    </div>
  );
};

export default AttendanceTracker;
